// ============================================================
// CLUBHUB – Formulare: Pruefung und Anzeige der Werte
// ============================================================
// Dieselbe Pruefung im Browser (sofort am Feld) und spaeter auf dem
// Server (BACKEND: die Server Action ruft `feldFehler` fuer jedes
// sichtbare Feld, bevor sie `FormSubmission` anlegt). Ein Formular,
// das etwas durchlaesst, was der Server verwirft, ist schlimmer als
// keines. Browsertauglich, ohne Abhaengigkeiten.
// ============================================================
import type { FeeTypeView, GroupView } from "@/lib/views";
import { EINWILLIGUNGEN } from "./einwilligungen";
import type { FieldValue, FormField, SepaValue, SubmissionData } from "./views";

// ── IBAN (ISO 13616, Pruefziffer Mod 97-10) ──────────────────
// Dieselbe Rechnung wie in den Einstellungen (`lib/einstellungen/check.ts`);
// Vorschlag im Bericht: nach `lib/sepa.ts` heben und gemeinsam nutzen.

function mod97(text: string): number {
  let rest = 0;
  for (const zeichen of text) {
    const code = zeichen.charCodeAt(0);
    const ziffern = code >= 65 && code <= 90 ? String(code - 55) : zeichen;
    for (const ziffer of ziffern) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest;
}

export function ibanKompakt(value: string): string {
  return value.replace(/\s+/g, "").toUpperCase();
}

export function ibanGueltig(value: string): boolean {
  const iban = ibanKompakt(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith("DE") && iban.length !== 22) return false;
  return mod97(iban.slice(4) + iban.slice(0, 4)) === 1;
}

/** In Vierergruppen, wie auf der Bankkarte: „DE89 3704 0044 0532 0130 00“. */
export function ibanLesbar(value: string): string {
  return ibanKompakt(value).replace(/(.{4})/g, "$1 ").trim();
}

/** Nur Land und die letzten vier Stellen: „DE89 •••• •••• •••• •••• 3000“. */
export function ibanVerdeckt(value: string): string {
  const iban = ibanKompakt(value);
  if (iban.length < 8) return iban;
  const mitte = iban.slice(4, -4).replace(/./g, "•");
  return ibanLesbar(iban.slice(0, 4) + mitte + iban.slice(-4));
}

/**
 * „Kolumbusstr. 9a“ → { strasse: "Kolumbusstr.", hausnummer: "9a" }.
 * Fuer Bestand aus ClubDesk, der beides in einem Feld fuehrt; neu
 * erfasst wird getrennt (`Contact.houseNumber`).
 */
export function strasseTeilen(zeile: string | null | undefined): { strasse: string; hausnummer: string } {
  const z = (zeile ?? "").trim();
  const m = /^(.*?)\s+(\d+\s*[a-zA-Z]?(?:\s*[-/]\s*\d+\s*[a-zA-Z]?)?)$/.exec(z);
  return m ? { strasse: m[1]!, hausnummer: m[2]!.replace(/\s+/g, "") } : { strasse: z, hausnummer: "" };
}

// ── Sonstige Pruefungen ──────────────────────────────────────

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function emailGueltig(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** Alter in vollen Jahren am Stichtag, aus einem ISO-Datum. */
export function alterAm(birthDate: string | null | undefined, stichtag: Date = new Date()): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}/.test(birthDate)) return null;
  const [y, m, d] = birthDate.slice(0, 10).split("-").map(Number) as [number, number, number];
  let alter = stichtag.getFullYear() - y;
  const monat = stichtag.getMonth() + 1;
  if (monat < m || (monat === m && stichtag.getDate() < d)) alter--;
  return alter;
}

export function istMinderjaehrig(birthDate: string | null | undefined, stichtag?: Date): boolean {
  const a = alterAm(birthDate, stichtag);
  return a !== null && a < 18;
}

export function istSepa(v: FieldValue | undefined): v is SepaValue {
  return Boolean(v) && typeof v === "object" && !Array.isArray(v) && "iban" in (v as object);
}

function leer(v: FieldValue | undefined): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "boolean") return v === false;
  return false;
}

/** Ist das Feld bei diesen Werten zu sehen? */
export function sichtbar(f: FormField, werte: SubmissionData): boolean {
  if (!f.showIf) return true;
  const w = werte[f.showIf.key];
  if (f.showIf.minor !== undefined) {
    const minor = typeof w === "string" && istMinderjaehrig(w);
    return f.showIf.minor ? minor : !minor && typeof w === "string" && w !== "";
  }
  if (f.showIf.equals !== undefined) return w === f.showIf.equals;
  return true;
}

/** Der Fehlersatz fuer ein Feld — oder `null`. */
export function feldFehler(f: FormField, v: FieldValue | undefined): string | null {
  if (f.type === "heading" || f.type === "info") return null;

  if (f.type === "sepa") {
    const s = istSepa(v) ? v : null;
    if (!f.required && (!s || (!s.iban && !s.holder))) return null;
    if (!s || !s.holder.trim()) return "Bitte den Namen des Kontoinhabers angeben.";
    if (!s.iban.trim()) return "Bitte die IBAN angeben.";
    if (!ibanGueltig(s.iban)) return "Diese IBAN stimmt nicht — bitte die Ziffern prüfen.";
    if (!s.agreed) return "Bitte das Mandat bestätigen.";
    return null;
  }

  if (leer(v)) {
    if (!f.required) return null;
    if (f.type === "checkbox" || f.type === "consent") return f.type === "consent" ? "Ohne diese Zustimmung können wir den Antrag nicht annehmen." : "Bitte bestätigen.";
    if (f.type === "select" || f.type === "radio" || f.type === "fee") return "Bitte eine Möglichkeit wählen.";
    if (f.type === "multiselect" || f.type === "groups") return "Bitte mindestens eine Möglichkeit wählen.";
    return "Bitte ausfüllen.";
  }

  const text = typeof v === "string" ? v.trim() : "";
  switch (f.type) {
    case "email":
      return emailGueltig(text) ? null : "Diese Mailadresse sieht nicht vollständig aus.";
    case "phone":
      return text.replace(/\D/g, "").length >= 6 ? null : "Bitte eine vollständige Nummer angeben.";
    case "number":
      return /^-?\d+([.,]\d+)?$/.test(text) ? null : "Bitte nur eine Zahl eingeben.";
    case "date": {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || Number.isNaN(new Date(text).getTime())) return "Bitte ein gültiges Datum angeben.";
      if (f.mapTo === "birthDate") {
        const a = alterAm(text);
        if (a === null || a < 0) return "Das Geburtsdatum liegt in der Zukunft.";
        if (a > 110) return "Bitte das Geburtsjahr prüfen.";
      }
      return null;
    }
    case "text":
      if (f.mapTo === "zip" && !/^\d{5}$/.test(text)) return "Eine Postleitzahl hat fünf Ziffern.";
      return null;
    default:
      return null;
  }
}

/** Alle sichtbaren Felder eines Schritts (oder des ganzen Formulars) pruefen. */
export function fehlerIn(fields: FormField[], werte: SubmissionData): Record<string, string> {
  const fehler: Record<string, string> = {};
  for (const f of fields) {
    if (!sichtbar(f, werte)) continue;
    const satz = feldFehler(f, werte[f.key]);
    if (satz) fehler[f.key] = satz;
  }
  return fehler;
}

// ── Anzeige ──────────────────────────────────────────────────

const DATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export type Bestand = { feeTypes: FeeTypeView[]; groups: GroupView[] };

/** Der Wert als lesbarer Text — fuer die Zusammenfassung und die Pruefung. */
export function wertText(f: FormField, v: FieldValue | undefined, bestand: Bestand, opts: { ibanOffen?: boolean } = {}): string {
  if (v === null || v === undefined || v === "") return "–";
  switch (f.type) {
    case "date":
      return typeof v === "string" && v ? DATUM.format(new Date(v)) : "–";
    case "select":
    case "radio":
      return f.options?.find((o) => o.value === v)?.label ?? String(v);
    case "multiselect":
      return Array.isArray(v) && v.length ? v.map((x) => f.options?.find((o) => o.value === x)?.label ?? x).join(", ") : "–";
    case "checkbox":
      return v === true ? "Ja" : "Nein";
    case "consent":
      return v === true ? "Erteilt" : "Nicht erteilt";
    case "fee": {
      const fee = bestand.feeTypes.find((x) => x.id === v);
      return fee ? fee.name : String(v);
    }
    case "groups":
      return Array.isArray(v) && v.length ? v.map((id) => bestand.groups.find((g) => g.id === id)?.name ?? id).join(", ") : "–";
    case "sepa":
      if (!istSepa(v)) return "–";
      return `${v.holder} · ${opts.ibanOffen ? ibanLesbar(v.iban) : ibanVerdeckt(v.iban)}${v.bic ? ` · ${v.bic}` : ""}`;
    default:
      return typeof v === "string" ? v : String(v);
  }
}

/** Der Titel einer Einwilligung oder das Label des Felds. */
export function feldTitel(f: FormField): string {
  if (f.type === "consent" && f.consent) return EINWILLIGUNGEN[f.consent].titel;
  return f.label;
}

/** Wer hat den Antrag gestellt — aus den gemappten Feldern gelesen. */
export function nameAus(fields: FormField[], data: SubmissionData): { vorname: string; nachname: string; email: string | null } {
  const wert = (k: string) => {
    const f = fields.find((x) => x.mapTo === k);
    const v = f ? data[f.key] : undefined;
    return typeof v === "string" ? v.trim() : "";
  };
  const name = fields.find((x) => x.key === "name");
  const ganz = name && typeof data[name.key] === "string" ? (data[name.key] as string) : "";
  return {
    vorname: wert("firstName") || ganz.split(" ")[0] || "",
    nachname: wert("lastName") || ganz.split(" ").slice(1).join(" "),
    email: wert("email") || (typeof data.email === "string" ? data.email : null) || null,
  };
}
